import React, {
  useEffect,
  useMemo,
} from 'react'

import PropTypes from 'prop-types'

import {
  IntlProvider as ReactIntlProvider,
  useIntl,
} from 'react-intl'

import FirstPaintGateContext from './FirstPaintGateContext'

import selectLocale from './lib/selectLocale'
import useFrozenValue from './hooks/useFrozenValue'
import useHasElapsed from './hooks/useHasElapsed'
import useLocaleStringsFile from './hooks/useLocaleStringsFile'

const DEFAULT_LOCALE = 'en'

const RTL_LANGUAGES = new Set([
  'ar',
  'he',
])


export default function IntlProvider(props) {
  const {
    children,
    localeFileURLTemplate,
    localeStringsTimeoutMS,
    preferredLocales,
    rootElem,
    supportedLocales,
    ...otherProps
  } = props

  const locale = useMemo(() => {
    return selectLocale({
      preferredLocales,
      supportedLocales,
    }) || DEFAULT_LOCALE
  }, [preferredLocales, supportedLocales])

  const isFirstPaintGated = Boolean(localeStringsTimeoutMS)

  // A locale that merely regionalizes the default language is never
  // gated. Its file only respells the strings react-intl is already
  // holding, so there is no repaint worth withholding a frame for.
  const [language] = locale.split('-')
  const isGateApplied = ( isFirstPaintGated && language !== DEFAULT_LOCALE )

  // The query is told that a gate is in play rather than whether this
  // locale ended up behind it, because it is one shared query and its
  // terms have to hold for every tree on the page.
  const {
    isFetched,
    localeStrings,
  } = useLocaleStringsFile({
    isFirstPaintGated,
    locale,
    urlTemplate: localeFileURLTemplate,
  })

  const hasWaitedLongEnough = useHasElapsed({
    delayMS: ( isGateApplied ) ? localeStringsTimeoutMS : void 0,
  })

  // We used to simply use <div dir={ dir }>{ children }</div>, below,
  // but that broke 'height: 100%' layouts in the dashboard. So, I
  // added 'height: 100%' to the div, but that broke some customer
  // sites that were using resource-blocker. So, it seems to me that
  // the best thing to do is to apply this property directly to the
  // DOM as high as possible.
  //
  // HOWEVER, since resource-blocker runs on other people's sites, we
  // don't want to modify the DOM outside of that area where we have
  // control. Imagine the customer's surprise when his
  // improperly-setup website was suddenly flipped to RTL because we
  // decided to show the end user Arabic. That would be really
  // bad. So, normally we'll default to document.body when we own the
  // entire DOM, but for other cases, we'll need to supply a
  // `rootElem` so we know precisely where to apply the `dir`
  // attribute.
  //
  // By the way, we're not using the CSS style `direction: rtl`
  // because that, apparently, is not inherited by table cells. See
  // https://developer.mozilla.org/en-US/docs/Web/CSS/direction for
  // more.
  useEffect(() => {
    const dir = ( RTL_LANGUAGES.has(locale) ) ? 'rtl' : 'ltr'

    // We used to use `document.body` as a default argument in the
    // props, but apparently when lazy-loading, `document.body` is
    // not available in the arguments...so we have to wait until
    // we get in here to do the fallback.
    const docRoot = rootElem || document.body

    docRoot.setAttribute('dir', dir)
  }, [locale, rootElem])

  // Rendering before the translated strings arrive means painting in
  // the default locale and then repainting in the visitor's language,
  // which resizes anything sized by its own copy and moves the
  // surrounding layout with it. Consumers that can't afford that pass
  // `localeStringsTimeoutMS` and place a <FirstPaintGate> around the copy,
  // which stays blank until the strings arrive and fails open to the
  // default locale once that many milliseconds have passed, so a slow
  // network delays the first paint rather than preventing it.
  //
  // Readiness is published rather than applied here so the consumer
  // decides where the blank part of its tree begins. Everything above its
  // gate keeps rendering, which is what a provider that fetches but paints
  // nothing needs: gating it too would put its request in series behind
  // this wait instead of alongside it.
  //
  // The wait is measured on our own clock rather than on the request,
  // because the request isn't ours alone: this query is shared with
  // every other provider on the page, and whichever of them fetches
  // first decides that request's retries and network mode.
  const areChildrenReady = ( !isGateApplied || isFetched || hasWaitedLongEnough )

  // For the same reason, whatever the children paint with is what they
  // keep. Strings arriving after the fail-open would resize the very
  // copy we just painted, and any provider sharing this query can bring
  // them in at any time — on a retry, on a remount, or when the browser
  // reconnects.
  const messages = useFrozenValue({
    isFrozen: ( isGateApplied && areChildrenReady ),
    value: localeStrings,
  })

  return (
    <ReactIntlProvider
      { ...otherProps }
      defaultLocale={ DEFAULT_LOCALE }
      locale={ locale }
      messages={ messages }
    >
      <FirstPaintGateContext.Provider
        value={ areChildrenReady }
      >
        { children }
      </FirstPaintGateContext.Provider>
    </ReactIntlProvider>
  )
}

IntlProvider.propTypes = {
  localeFileURLTemplate: PropTypes.string.isRequired,
  preferredLocales: PropTypes.arrayOf(PropTypes.string).isRequired,
  supportedLocales: PropTypes.arrayOf(PropTypes.string).isRequired,

  children: PropTypes.node,
  localeStringsTimeoutMS: PropTypes.number,
  rootElem: PropTypes.shape({
    setAttribute: PropTypes.func.isRequired,
  }),
}

export {
  useIntl
}
